import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

const APP_BASE_URL = process.env.REACT_APP_BASE_URL || '';

async function fetchArtistByToken(token) {
  const res = await fetch(`${APP_BASE_URL}/api/artist-profile-approval?token=${encodeURIComponent(token)}`);
  const data = await res.json();
  if (!res.ok) throw Object.assign(new Error(data.error || 'Error al cargar el perfil.'), { alreadyApproved: data.alreadyApproved, slug: data.slug });
  return data;
}

async function submitAction(token, action, notes) {
  const res = await fetch(`${APP_BASE_URL}/api/artist-profile-approval`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, action, notes }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al procesar la acción.');
  return data;
}

const cardStyle = {
  maxWidth: '640px',
  margin: '3rem auto',
  padding: '0 1.25rem',
  fontFamily: 'Arial, sans-serif',
  color: '#333',
};

const sectionStyle = {
  background: 'white',
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  padding: '2rem',
  marginBottom: '1.5rem',
};

const btnPrimary = {
  display: 'inline-block',
  background: '#1a1a2e',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  padding: '0.75rem 1.75rem',
  fontSize: '1rem',
  fontWeight: '700',
  cursor: 'pointer',
};

const btnSecondary = {
  display: 'inline-block',
  background: 'none',
  color: '#555',
  border: '1.5px solid #ccc',
  borderRadius: '8px',
  padding: '0.75rem 1.75rem',
  fontSize: '1rem',
  fontWeight: '600',
  cursor: 'pointer',
};

function ProfilePreview({ artist }) {
  const accent = artist.accentColor || '#1a1a2e';
  const showcaseImages = [artist.showcaseImage1Url, artist.showcaseImage2Url, artist.showcaseImage3Url].filter(Boolean);
  const processImages = Array.isArray(artist.processImages) ? artist.processImages.filter(Boolean) : [];
  const heroQuote = artist.manifesto || null;
  const heroRights = !heroQuote ? (artist.rightsReserved || null) : null;
  const heroFallback = artist.coverImageUrl || (showcaseImages.length === 0 ? artist.profileImageUrl : null);

  return (
    <div>
      <p style={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic', margin: '0 0 0.75rem' }}>
        Vista previa aproximada — el perfil publicado puede tener diferencias de estilo y animaciones.
      </p>

      {/* ── Hero band ──────────────────────────────────────────────────────── */}
      <div style={{ background: accent, borderRadius: '10px', overflow: 'hidden', marginBottom: '1.25rem', position: 'relative', minHeight: '220px' }}>
        {showcaseImages.length > 0 ? (
          <div style={{ display: 'flex', height: '220px', gap: '3px' }}>
            {showcaseImages.map((url, i) => (
              <img
                key={i}
                src={url}
                alt={`Obra ${i + 1}`}
                style={{ flex: 1, minWidth: 0, objectFit: 'cover', display: 'block' }}
              />
            ))}
          </div>
        ) : heroFallback ? (
          <img
            src={heroFallback}
            alt={artist.artistName}
            style={{ width: '100%', height: '220px', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{ height: '220px' }} />
        )}

        {/* Bottom-left: artist name + creator caption */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.65))', padding: '2rem 1.25rem 1rem', color: 'white' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '900', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
            {artist.artistName}
          </div>
          {artist.creatorName && (
            <div style={{ fontSize: '0.83rem', opacity: 0.85, marginTop: '0.25rem' }}>
              por {artist.creatorName}
            </div>
          )}
        </div>

        {/* Top-right: quote / rights / instagram */}
        {(heroQuote || heroRights || artist.instagramHandle) && (
          <div style={{ position: 'absolute', top: '1rem', right: '1rem', maxWidth: '52%', textAlign: 'right', color: 'white' }}>
            {heroQuote && (
              <blockquote style={{ margin: '0 0 0.4rem', fontStyle: 'italic', fontSize: '0.82rem', opacity: 0.92, lineHeight: 1.45 }}>
                "{heroQuote}"
              </blockquote>
            )}
            {heroRights && (
              <p style={{ margin: '0 0 0.4rem', fontSize: '0.75rem', opacity: 0.75, lineHeight: 1.4 }}>
                {heroRights}
              </p>
            )}
            {artist.instagramHandle && (
              <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>@{artist.instagramHandle}</span>
            )}
          </div>
        )}
      </div>

      {/* ── Bio section ────────────────────────────────────────────────────── */}
      {(artist.about || (artist.creatorName && artist.creatorAbout)) && (
        <div style={sectionStyle}>
          <p style={{ fontSize: '0.72rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 0.9rem' }}>
            {artist.creatorName ? `El proyecto — ${artist.artistName}` : 'Sobre el artista'}
          </p>

          <div style={{ overflow: 'hidden' }}>
            {artist.profileImageUrl && (
              <img
                src={artist.profileImageUrl}
                alt={artist.artistName}
                style={{ width: '72px', height: '72px', borderRadius: '50%', objectFit: 'cover', border: `3px solid ${accent}`, float: 'right', marginLeft: '1rem', marginBottom: '0.5rem' }}
              />
            )}
            {artist.about && (
              <p style={{ lineHeight: '1.7', margin: 0, whiteSpace: 'pre-wrap' }}>{artist.about}</p>
            )}
          </div>

          {/* Creator sub-section */}
          {artist.creatorName && artist.creatorAbout && (
            <>
              <hr style={{ margin: '1.25rem 0', border: 'none', borderTop: '1px solid #eee' }} />
              <p style={{ fontSize: '0.72rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 0.9rem' }}>
                Sobre {artist.creatorName}
              </p>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                {artist.creatorProfileImageUrl && (
                  <img
                    src={artist.creatorProfileImageUrl}
                    alt={artist.creatorName}
                    style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid #eee' }}
                  />
                )}
                <p style={{ lineHeight: '1.7', margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.92rem', color: '#444' }}>
                  {artist.creatorAbout}
                </p>
              </div>
            </>
          )}

          {artist.signatureSvgUrl && (
            <img
              src={artist.signatureSvgUrl}
              alt={`Firma de ${artist.artistName}`}
              style={{ maxHeight: '56px', maxWidth: '160px', marginTop: '1.25rem', display: 'block' }}
            />
          )}
        </div>
      )}

      {/* ── Studio video ───────────────────────────────────────────────────── */}
      {artist.studioVideoUrl && (
        <div style={sectionStyle}>
          <p style={{ fontSize: '0.72rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 0.75rem' }}>
            En el estudio
          </p>
          {artist.studioVideoCaption && (
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#555', lineHeight: 1.55 }}>
              {artist.studioVideoCaption}
            </p>
          )}
          <video
            src={artist.studioVideoUrl}
            controls
            muted
            playsInline
            style={{ width: '100%', borderRadius: '8px', maxHeight: '320px', objectFit: 'cover', background: '#111', display: 'block' }}
          />
        </div>
      )}

      {/* ── Process images ─────────────────────────────────────────────────── */}
      {processImages.length > 0 && (
        <div style={sectionStyle}>
          <p style={{ fontSize: '0.72rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 0.75rem' }}>
            Proceso
          </p>
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
            {processImages.map((url, i) => (
              <img
                key={i}
                src={url}
                alt={`Proceso ${i + 1}`}
                style={{ height: '110px', aspectRatio: '1', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Website ────────────────────────────────────────────────────────── */}
      {artist.websiteUrl && (
        <div style={{ textAlign: 'center', padding: '0.25rem 0 0.5rem' }}>
          <a
            href={artist.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: accent, fontSize: '0.88rem', wordBreak: 'break-all' }}
          >
            {artist.websiteUrl}
          </a>
        </div>
      )}
    </div>
  );
}

export default function ArtistApprovalPage() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [artist, setArtist]       = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [alreadyApproved, setAlreadyApproved] = useState(false);
  const [approvedSlug, setApprovedSlug]       = useState(null);
  const [loading, setLoading]     = useState(true);

  const [action, setAction]       = useState(null); // null | 'approve' | 'request_changes'
  const [notes, setNotes]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone]           = useState(false);
  const [doneMsg, setDoneMsg]     = useState('');

  useEffect(() => {
    if (!token) {
      setLoadError('Enlace inválido. Revisa el correo que recibiste.');
      setLoading(false);
      return;
    }

    fetchArtistByToken(token)
      .then(setArtist)
      .catch((err) => {
        if (err.alreadyApproved) {
          setAlreadyApproved(true);
          setApprovedSlug(err.slug);
        } else {
          setLoadError(err.message);
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (action === 'request_changes' && !notes.trim()) {
      setSubmitError('Por favor describe los cambios que necesitas.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submitAction(token, action, notes);
      setDoneMsg(res.message);
      setDone(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center', paddingTop: '4rem' }}>
        <p style={{ color: '#888' }}>Cargando tu perfil...</p>
      </div>
    );
  }

  if (alreadyApproved) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={sectionStyle}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <h2 style={{ margin: '0 0 0.75rem' }}>Perfil ya aprobado</h2>
          <p style={{ color: '#555', marginBottom: '1.5rem' }}>Tu perfil de artista ya está publicado en Bluppimart.</p>
          {approvedSlug && (
            <Link to={`/artist/${approvedSlug}`} style={{ ...btnPrimary, textDecoration: 'none' }}>
              Ver mi perfil
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={sectionStyle}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ margin: '0 0 0.75rem' }}>Enlace no válido</h2>
          <p style={{ color: '#555' }}>{loadError}</p>
          <p style={{ color: '#888', fontSize: '0.88rem', marginTop: '1rem' }}>
            Si crees que esto es un error, contacta al equipo de Bluppimart.
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={sectionStyle}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {action === 'approve' ? '🎉' : '📩'}
          </div>
          <h2 style={{ margin: '0 0 0.75rem' }}>
            {action === 'approve' ? '¡Perfil aprobado!' : 'Comentarios enviados'}
          </h2>
          <p style={{ color: '#555' }}>{doneMsg}</p>
          {action === 'approve' && artist?.slug && (
            <Link to={`/artist/${artist.slug}`} style={{ ...btnPrimary, textDecoration: 'none', display: 'inline-block', marginTop: '1.25rem' }}>
              Ver mi perfil
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', margin: '0 0 0.3rem', fontFamily: "'Gambado Sans', sans-serif" }}>
          Revisa tu perfil de artista
        </h1>
        <p style={{ color: '#666', margin: 0, fontSize: '0.93rem' }}>
          El equipo de Bluppimart preparó este perfil para ti. Revísalo y apruébalo para publicarlo,
          o envíanos tus comentarios si algo necesita cambio.
        </p>
      </div>

      <ProfilePreview artist={artist} />

      {action === null && (
        <div style={{ ...sectionStyle, display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button style={btnPrimary} onClick={() => setAction('approve')}>
            ✓ Aprobar y publicar mi perfil
          </button>
          <button style={btnSecondary} onClick={() => setAction('request_changes')}>
            Solicitar cambios
          </button>
        </div>
      )}

      {action === 'approve' && (
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 0.75rem' }}>Confirmar aprobación</h3>
          <p style={{ color: '#555', margin: '0 0 1.25rem' }}>
            Al aprobar, tu perfil quedará visible públicamente en Bluppimart.
          </p>
          {submitError && (
            <div style={{ color: '#dc3545', marginBottom: '0.75rem', fontSize: '0.9rem' }}>{submitError}</div>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button style={btnPrimary} onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Aprobando...' : 'Confirmar aprobación'}
            </button>
            <button style={btnSecondary} onClick={() => { setAction(null); setSubmitError(null); }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {action === 'request_changes' && (
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 0.75rem' }}>¿Qué necesita cambiar?</h3>
          <p style={{ color: '#555', margin: '0 0 0.75rem', fontSize: '0.9rem' }}>
            Describe con detalle qué información está incorrecta o qué quisieras actualizar.
          </p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ej: Mi foto de perfil es incorrecta, la descripción tiene un error ortográfico en el segundo párrafo..."
            maxLength={2000}
            rows={5}
            style={{ width: '100%', padding: '0.65rem 0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', resize: 'vertical' }}
          />
          <div style={{ fontSize: '0.78rem', color: '#888', textAlign: 'right', marginTop: '0.25rem' }}>
            {notes.length}/2000
          </div>
          {submitError && (
            <div style={{ color: '#dc3545', margin: '0.75rem 0 0', fontSize: '0.9rem' }}>{submitError}</div>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            <button style={btnPrimary} onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Enviando...' : 'Enviar comentarios'}
            </button>
            <button style={btnSecondary} onClick={() => { setAction(null); setSubmitError(null); }}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
